import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { isCancelled, isThrottled, throttleMessage, validationErrors } from '@/api/client'
import { requestCalculation } from '@/services/calculation'
import type { CalculationInput, CalculationResult, CalculationStatus } from '@/types/calculation'

/**
 * Holds the calculation for the whole tool. A store instead of a composable, because the input
 * step, the estimation modal and the result screen all read the same state and a store is a
 * singleton by construction.
 *
 * Nothing is persisted: capi stores no state either, so a refresh starts over.
 */
export const useCalculationStore = defineStore('calculation', () => {
  const status = ref<CalculationStatus | null>(null)
  const result = ref<CalculationResult | null>(null)
  /** The messages capi returned, keyed by its own field names. The input store places them. */
  const fieldErrors = ref<Record<string, string>>({})
  /** What capi said about the throttling, empty when it sent nothing usable. */
  const throttleNotice = ref('')

  const isCalculating = computed(() => status.value === 'calculating')

  /**
   * A visitor who gains from the scheme ending: the netting is worth less to them than the
   * redelivery costs that come with it. The whole result screen turns around for them (#6331).
   */
  const paysLess = computed(() => status.value === 'calculated' && (result.value?.difference?.monthly ?? 0) < 0)

  async function calculate(input: CalculationInput): Promise<void> {
    status.value = 'calculating'
    result.value = null
    fieldErrors.value = {}
    throttleNotice.value = ''

    try {
      const response = await requestCalculation(input)

      result.value = response
      // A missing tariff is a valid answer carrying a message instead of amounts, not a failure.
      status.value = response.status === 'CALCULATED' ? 'calculated' : 'unavailable'
    } catch (error) {
      // A submit that replaces an earlier one aborts it. That is not a failure: the newer call is
      // still running and will set the status itself.
      if (isCancelled(error)) {
        return
      }

      // The limiter answers without field messages, so it is checked before them.
      if (isThrottled(error)) {
        throttleNotice.value = throttleMessage(error)
        status.value = 'throttled'

        return
      }

      // Field messages mean capi rejected the input. Anything else is unreachable or broken and
      // carries no message worth showing.
      fieldErrors.value = validationErrors(error)
      status.value = Object.keys(fieldErrors.value).length > 0 ? 'invalid' : 'failed'
    }
  }

  function reset(): void {
    status.value = null
    result.value = null
    fieldErrors.value = {}
    throttleNotice.value = ''
  }

  return { status, result, fieldErrors, throttleNotice, isCalculating, paysLess, calculate, reset }
})
